import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Line } from 'react-chartjs-2';
import api from '../../api';
import {
  MetricCard,
  NO_DATA,
  fmtDay,
  fmtGbp,
  fmtHours,
  fmtMinutes,
  fmtPct,
  fmtUsd,
  lineOpts,
} from './scaling/scalingCard';
import {
  HumanTaskHold,
  HumanTaskHoldCard,
  IdleHeldTasks,
  IdleHeldTasksCard,
} from './scaling/HumanTaskHoldCards';
import {
  RELEASE_MARKER_PLUGINS,
  ReleaseMarker,
  ReleaseMarkerLegend,
  withReleaseMarkers,
} from './scaling/releaseMarkers';
import './scaling-dashboard.scss';

/**
 * Scaling tab of Platform Stats — the default view.
 *
 * The seven headline areas Ben approved for the 60 → 500 user push, in order:
 *   1. WAU — the core pulse metric
 *   2. Activation rate — new signups who send a real message within 48h
 *   3. D7 retention — new users still active seven days after signup
 *   4. Out-of-credits behaviour — top up / refer / nothing, plus median
 *      time-to-action after hitting the wall
 *   5. Cost per task — blended AI + human-task cost per completed task
 *   6. Accounts within 10% of their credit ceiling — the early warning for #4
 *   7. Speed — three separate figures, never blended: human-task pickup wait,
 *      browser-intervention wait, and total task completion time
 *
 * The headline row stays deliberately calm: one number per area, with the
 * definition, cohort sizes and known data limitations tucked into a
 * "How it's measured" disclosure on each card. Metrics with nothing to measure
 * render as "No data yet" rather than a confident 0%.
 */

const TIMEFRAMES = [7, 14, 30, 60, 90];

/** GET /platform-stats/scaling — local until the shape settles. */
interface SpeedFigure {
  definition: string;
  avgMinutes: number | null;
  sampleSize: number;
}

interface ScalingResponse {
  success: boolean;
  days: number;
  generatedAt: string;
  wau: {
    definition: string;
    current: number;
    previous: number | null;
    changePct: number | null;
    series: Array<{ weekStart: string; wau: number }>;
  };
  activation: {
    definition: string;
    windowHours: number;
    cohort: number;
    activated: number;
    ratePct: number | null;
  };
  d7Retention: {
    definition: string;
    retentionDay: number;
    cohort: number;
    retained: number;
    ratePct: number | null;
  };
  outOfCredits: {
    definition: string;
    threshold: number;
    events: number;
    accounts: number;
    toppedUp: number;
    referred: number;
    noAction: number;
    toppedUpPct: number | null;
    referredPct: number | null;
    noActionPct: number | null;
    medianHoursToAction: number | null;
    actedCount: number;
    limitation: string;
  };
  humanTaskCost: {
    definition: string;
    windowDays: number;
    currency: 'GBP';
    rateGbpPerHour: number;
    gbpUsdRate: number;
    sampleSize: number;
    avgMinutes: number | null;
    avgCostGbp: number | null;
    avgCostUsd: number | null;
    percentiles: Array<{
      label: string;
      minutes: number;
      costGbp: number;
      costUsd: number;
    }>;
    limitation: string;
  };
  costPerTask: {
    definition: string;
    aiCostUsd: number;
    humanMinutes: number;
    completedTasks: number;
    humanCostUsd: number;
    totalCostUsd: number;
    blendedCostPerTaskUsd: number | null;
    aiCostPerTaskUsd: number | null;
    humanCostPerTaskUsd: number | null;
    humanCostGbpPerHour: number;
    humanCostUsdPerMinute: number;
    gbpUsdRate: number;
    humanTasksCounted: number;
    limitation: string;
  };
  nearCeiling: {
    definition: string;
    fraction: number;
    accounts: number;
    nearCeiling: number;
    alreadyOutOfCredits: number;
    ratePct: number | null;
  };
  kFactor: {
    definition: string;
    windowDays: number;
    eligibleInviters: number;
    invitesInWindow: number;
    matureInvites: number;
    convertedInvites: number;
    retroAttributedInvites: number;
    invitesPerInviter: number | null;
    conversionRate: number | null;
    kFactor: number | null;
    maturityDays: number;
    minMatureInvites: number;
    /** Populated only when the K-factor itself is null. */
    reason: string | null;
    limitation: string;
  };
  referralRate: {
    definition: string;
    eligibleAccounts: number;
    accountsWithInvite: number;
    accountsWithAttributableConversion: number;
    invitesFromEligibleAccounts: number;
    ratePct: number | null;
    convertedRatePct: number | null;
    maturityDays: number;
    limitation: string;
  };
  speed: {
    humanTaskPickup: SpeedFigure;
    browserIntervention: SpeedFigure;
    /**
     * Completion time is skewed: a handful of tasks are classified days after
     * they start, so the median leads and the mean is shown beside it.
     */
    taskCompletion: SpeedFigure & {
      medianMinutes: number | null;
      p90Minutes: number | null;
      measuredTo: string;
    };
  };
  humanTaskHold: HumanTaskHold;
  idleHeldTasks: IdleHeldTasks;
  releaseMarkers: ReleaseMarker[];
}

/** GET /platform-stats/scaling/out-of-credits/no-action */
interface NoActionAccount {
  companyId: number;
  companyName: string | null;
  userNames: string | null;
  hitWallAt: string;
  thresholdWarned: number | null;
  balance: number | null;
}

interface NoActionResponse {
  success: boolean;
  days: number;
  definition: string;
  accounts: NoActionAccount[];
  limitation: string;
}

/**
 * Percentile breakdown behind the standalone human-task cost figure: what the
 * typical task costs vs the tail, so a long p99 can't hide inside the mean.
 */
const HumanTaskCostModal: FC<{
  data: ScalingResponse['humanTaskCost'];
  days: number;
  onClose: () => void;
}> = ({ data, days, onClose }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className='scaling-modal-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className='scaling-modal'
        role='dialog'
        aria-modal='true'
        aria-label='Human task cost breakdown'
      >
        <div className='scaling-modal-header'>
          <h4>Human task cost</h4>
          <button
            type='button'
            className='scaling-modal-close'
            onClick={onClose}
            aria-label='Close human task cost breakdown'
          >
            ×
          </button>
        </div>

        <p className='scaling-modal-meta'>
          {data.sampleSize} human task{data.sampleSize === 1 ? '' : 's'} with a
          recorded duration · last {days} days · costed in {data.currency} at £
          {data.rateGbpPerHour}/hour
        </p>

        <div className='scaling-modal-headline'>
          <span className='scaling-modal-headline-value'>
            {fmtGbp(data.avgCostGbp)}
          </span>
          <span className='scaling-modal-headline-label'>
            Average cost per human task ({fmtMinutes(data.avgMinutes)} average
            completion time)
          </span>
        </div>

        {data.percentiles.length === 0 ? (
          <p className='scaling-modal-meta'>{NO_DATA}</p>
        ) : (
          <table className='scaling-modal-table'>
            <thead>
              <tr>
                <th>Percentile</th>
                <th>Completion time</th>
                <th>Cost (GBP)</th>
                <th>Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {data.percentiles.map((p) => (
                <tr key={p.label}>
                  <th scope='row'>{p.label}</th>
                  <td>{fmtMinutes(p.minutes)}</td>
                  <td>{fmtGbp(p.costGbp)}</td>
                  <td>{fmtUsd(p.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className='scaling-modal-note'>
          {data.definition} USD shown at {data.gbpUsdRate} USD/GBP.
        </p>
        <p className='scaling-limitation'>Limitation: {data.limitation}</p>
      </div>
    </div>
  );
};

/**
 * Card 4 drill-down: exactly who hit the credit wall and then did nothing, so
 * the percentage can be followed up rather than just read.
 */
const NoActionModal: FC<{ days: number; onClose: () => void }> = ({
  days,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState<NoActionAccount[] | null>(null);
  const [meta, setMeta] = useState<{
    definition: string;
    limitation: string;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<NoActionResponse>(
          `/platform-stats/scaling/out-of-credits/no-action?days=${days}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setRows(res.data.accounts);
        setMeta({
          definition: res.data.definition,
          limitation: res.data.limitation,
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, days]);

  return (
    <div
      className='scaling-modal-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className='scaling-modal scaling-modal--wide'
        role='dialog'
        aria-modal='true'
        aria-label='Accounts that hit the credit wall and did nothing'
      >
        <div className='scaling-modal-header'>
          <h4>Hit the wall, did nothing</h4>
          <button
            type='button'
            className='scaling-modal-close'
            onClick={onClose}
            aria-label='Close no-action account list'
          >
            ×
          </button>
        </div>

        <p className='scaling-modal-meta'>
          No top-up and no referral since hitting the wall · last {days} days
        </p>

        {failed && (
          <p className='scaling-modal-meta'>Failed to load accounts</p>
        )}
        {!failed && rows == null && (
          <p className='scaling-modal-meta'>Loading accounts…</p>
        )}
        {rows != null && rows.length === 0 && (
          <p className='scaling-modal-meta'>
            No accounts hit the wall without acting in this window.
          </p>
        )}

        {rows != null && rows.length > 0 && (
          <table className='scaling-modal-table'>
            <thead>
              <tr>
                <th>Account</th>
                <th>Hit the wall</th>
                <th>Warned at</th>
                <th>Balance now</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.companyId}>
                  <th scope='row'>
                    {r.companyName ?? `Company ${r.companyId}`}
                    <span className='scaling-modal-subtle'>
                      {' '}
                      #{r.companyId}
                    </span>
                    {r.userNames && (
                      <span className='scaling-modal-names' title={r.userNames}>
                        {r.userNames}
                      </span>
                    )}
                  </th>
                  <td>{new Date(r.hitWallAt).toLocaleDateString('en-GB')}</td>
                  <td>
                    {r.thresholdWarned == null
                      ? '—'
                      : `${r.thresholdWarned} credits`}
                  </td>
                  <td>{r.balance == null ? '—' : Math.round(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {meta && (
          <>
            <p className='scaling-modal-note'>{meta.definition}</p>
            <p className='scaling-limitation'>Limitation: {meta.limitation}</p>
          </>
        )}
      </div>
    </div>
  );
};

const ScalingDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ScalingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [humanCostOpen, setHumanCostOpen] = useState(false);
  const [noActionOpen, setNoActionOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<ScalingResponse>(
        `/platform-stats/scaling?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch {
      setError('Failed to load scaling stats');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const wauWeeks = useMemo(
    () => (data ? data.wau.series.map((p) => p.weekStart) : []),
    [data],
  );
  const wauMarkers = useMemo(() => data?.releaseMarkers ?? [], [data]);
  const wauOpts = useMemo(
    () => withReleaseMarkers(lineOpts, wauMarkers, wauWeeks),
    [wauMarkers, wauWeeks],
  );

  const wauTrend = (() => {
    if (!data || data.wau.changePct == null) return undefined;
    const pct = data.wau.changePct;
    return {
      label: `${pct > 0 ? '+' : ''}${pct}% vs last week`,
      direction: (pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat') as
        | 'up'
        | 'down'
        | 'flat',
    };
  })();

  return (
    <div className='scaling-dashboard'>
      <div className='scaling-controls'>
        <p className='scaling-intro'>
          Scaling from ~60 to 500 users — the seven headline signals.
        </p>
        <div className='day-selector'>
          {TIMEFRAMES.map((d) => (
            <button
              key={d}
              className={`day-btn ${days === d ? 'active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {error && <div className='msg error'>{error}</div>}

      {loading && !data && <div className='loading'>Loading...</div>}

      {data && (
        <div className='scaling-grid'>
          {/* 1 — WAU */}
          <MetricCard
            index={1}
            title='Weekly Active Users'
            value={`${data.wau.current}`}
            sub='Distinct users who sent a message in the last 7 days'
            trend={wauTrend}
            className='scaling-card--chart'
            details={<p>{data.wau.definition}</p>}
          >
            {data.wau.series.length > 1 && (
              <>
                <div className='scaling-chart'>
                  <Line
                    options={wauOpts}
                    plugins={RELEASE_MARKER_PLUGINS}
                    data={{
                      labels: data.wau.series.map((p) => fmtDay(p.weekStart)),
                      datasets: [
                        {
                          data: data.wau.series.map((p) => p.wau),
                          borderColor: 'rgb(59, 130, 246)',
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          fill: true,
                          tension: 0.35,
                          pointRadius: 2,
                          borderWidth: 2,
                        },
                      ],
                    }}
                  />
                </div>
                <ReleaseMarkerLegend
                  markers={wauMarkers}
                  labelDates={wauWeeks}
                />
              </>
            )}
          </MetricCard>

          {/* 2 — Activation */}
          <MetricCard
            index={2}
            title='Activation rate'
            value={fmtPct(data.activation.ratePct)}
            sub={`${data.activation.activated} of ${data.activation.cohort} new signups messaged within ${data.activation.windowHours}h`}
            details={
              <>
                <p>{data.activation.definition}</p>
                <p>
                  Signups from the last {data.days} days that have had a full{' '}
                  {data.activation.windowHours}h to respond; newer signups are
                  excluded so they can&apos;t drag the rate down.
                </p>
              </>
            }
          />

          {/* 3 — D7 retention */}
          <MetricCard
            index={3}
            title={`D${data.d7Retention.retentionDay} retention`}
            value={fmtPct(data.d7Retention.ratePct)}
            sub={`${data.d7Retention.retained} of ${data.d7Retention.cohort} new users active on day ${data.d7Retention.retentionDay}`}
            details={
              <>
                <p>{data.d7Retention.definition}</p>
                <p>
                  Only signups old enough to have a complete day-
                  {data.d7Retention.retentionDay} window are in the cohort.
                </p>
              </>
            }
          />

          {/* 4 — Out-of-credits behaviour */}
          <MetricCard
            index={4}
            title='Out-of-credits behaviour'
            value={
              data.outOfCredits.events === 0
                ? NO_DATA
                : `${data.outOfCredits.events} wall hit${
                    data.outOfCredits.events === 1 ? '' : 's'
                  } across ${data.outOfCredits.accounts} account${
                    data.outOfCredits.accounts === 1 ? '' : 's'
                  }`
            }
            sub={
              data.outOfCredits.medianHoursToAction == null
                ? 'No account has acted yet'
                : `Median ${fmtHours(
                    data.outOfCredits.medianHoursToAction,
                  )} to first action`
            }
            className='scaling-card--wide'
            details={
              <>
                <p>{data.outOfCredits.definition}</p>
                <p className='scaling-limitation'>
                  Limitation: {data.outOfCredits.limitation}
                </p>
              </>
            }
          >
            <div className='scaling-split'>
              <div className='scaling-split-item'>
                <span className='scaling-split-value'>
                  {fmtPct(data.outOfCredits.toppedUpPct)}
                </span>
                <span className='scaling-split-label'>Topped up</span>
                <span className='scaling-split-meta'>
                  {data.outOfCredits.toppedUp} wall hits
                </span>
              </div>
              <div className='scaling-split-item'>
                <span className='scaling-split-value'>
                  {fmtPct(data.outOfCredits.referredPct)}
                </span>
                <span className='scaling-split-label'>Referred someone</span>
                <span className='scaling-split-meta'>
                  {data.outOfCredits.referred} wall hits
                </span>
              </div>
              <button
                type='button'
                className='scaling-split-item scaling-split-item--clickable'
                onClick={() => setNoActionOpen(true)}
              >
                <span className='scaling-split-value'>
                  {fmtPct(data.outOfCredits.noActionPct)}
                </span>
                <span className='scaling-split-label'>Did nothing</span>
                <span className='scaling-split-meta'>
                  {data.outOfCredits.noAction} wall hits · tap to see who
                </span>
              </button>
            </div>
          </MetricCard>

          {/* 5 — Cost per task */}
          <MetricCard
            index={5}
            title='Cost per task'
            value={fmtUsd(data.costPerTask.blendedCostPerTaskUsd)}
            sub={`Blended AI + human cost across ${data.costPerTask.completedTasks} completed tasks`}
            details={
              <>
                <p>{data.costPerTask.definition}</p>
                <ul>
                  <li>
                    AI: {fmtUsd(data.costPerTask.aiCostUsd)} total ·{' '}
                    {fmtUsd(data.costPerTask.aiCostPerTaskUsd)} per task
                  </li>
                  <li>
                    Human: {fmtUsd(data.costPerTask.humanCostUsd)} total ·{' '}
                    {fmtUsd(data.costPerTask.humanCostPerTaskUsd)} per task (
                    {Math.round(data.costPerTask.humanMinutes)} min across{' '}
                    {data.costPerTask.humanTasksCounted} human tasks at £
                    {data.costPerTask.humanCostGbpPerHour}/hour)
                  </li>
                </ul>
                <p>
                  Ops time is costed at the actual £
                  {data.costPerTask.humanCostGbpPerHour}/hour rate, converted to
                  USD at {data.costPerTask.gbpUsdRate} to blend with LLM spend.
                </p>
                <p className='scaling-limitation'>
                  Limitation: {data.costPerTask.limitation}
                </p>
              </>
            }
          >
            <button
              type='button'
              className='scaling-drill-figure'
              onClick={() => setHumanCostOpen(true)}
            >
              <span className='scaling-drill-value'>
                {fmtGbp(data.humanTaskCost.avgCostGbp)}
              </span>
              <span className='scaling-drill-label'>
                Human task cost · average of{' '}
                {fmtMinutes(data.humanTaskCost.avgMinutes)} at £
                {data.humanTaskCost.rateGbpPerHour}/hour
              </span>
              <span className='scaling-drill-meta'>
                {data.humanTaskCost.sampleSize} tasks · tap for p50 / p90 / p99
              </span>
            </button>
          </MetricCard>

          {/* 6 — Accounts near their credit ceiling */}
          <MetricCard
            index={6}
            title={`Within ${data.nearCeiling.fraction * 100}% of credit ceiling`}
            value={fmtPct(data.nearCeiling.ratePct)}
            sub={`${data.nearCeiling.nearCeiling} of ${data.nearCeiling.accounts} accounts · ${data.nearCeiling.alreadyOutOfCredits} already at zero`}
            details={
              <>
                <p>{data.nearCeiling.definition}</p>
                <p>
                  Accounts with no credit allocation on record are left out of
                  the denominator rather than counted as healthy.
                </p>
              </>
            }
          />

          {/* 7 — Speed, kept as three separate figures */}
          <MetricCard
            index={7}
            title='Speed breakdown'
            value={fmtMinutes(data.speed.taskCompletion.medianMinutes)}
            sub={`Median total task completion time (mean ${fmtMinutes(
              data.speed.taskCompletion.avgMinutes,
            )}, skewed by a long tail) — the other two waits are shown separately, never blended`}
            className='scaling-card--wide'
            details={
              <ul>
                <li>{data.speed.humanTaskPickup.definition}</li>
                <li>{data.speed.browserIntervention.definition}</li>
                <li>{data.speed.taskCompletion.definition}</li>
              </ul>
            }
          >
            <div className='scaling-split'>
              <div className='scaling-split-item'>
                <span className='scaling-split-value'>
                  {fmtMinutes(data.speed.humanTaskPickup.avgMinutes)}
                </span>
                <span className='scaling-split-label'>
                  Human task pickup wait
                </span>
                <span className='scaling-split-meta'>
                  {data.speed.humanTaskPickup.sampleSize} tasks
                </span>
              </div>
              <div className='scaling-split-item'>
                <span className='scaling-split-value'>
                  {fmtMinutes(data.speed.browserIntervention.avgMinutes)}
                </span>
                <span className='scaling-split-label'>
                  Browser intervention wait
                </span>
                <span className='scaling-split-meta'>
                  {data.speed.browserIntervention.sampleSize} interventions
                </span>
              </div>
              <div className='scaling-split-item'>
                <span className='scaling-split-value'>
                  {fmtMinutes(data.speed.taskCompletion.medianMinutes)}
                </span>
                <span className='scaling-split-label'>
                  Total task completion (median)
                </span>
                <span className='scaling-split-meta'>
                  {data.speed.taskCompletion.sampleSize} tasks · p90{' '}
                  {fmtMinutes(data.speed.taskCompletion.p90Minutes)} · mean{' '}
                  {fmtMinutes(data.speed.taskCompletion.avgMinutes)}
                </span>
              </div>
            </div>
          </MetricCard>

          {/* 8 — K-factor (viral coefficient) */}
          <MetricCard
            index={8}
            title='K-factor (viral coefficient)'
            value={
              data.kFactor.kFactor == null
                ? NO_DATA
                : data.kFactor.kFactor.toFixed(2)
            }
            sub={
              data.kFactor.reason ??
              `${data.kFactor.matureInvites} mature invites from ${data.kFactor.eligibleInviters} active users × ${fmtPct(
                data.kFactor.conversionRate == null
                  ? null
                  : Math.round(data.kFactor.conversionRate * 1000) / 10,
              )} converting`
            }
            details={
              <>
                <p>{data.kFactor.definition}</p>
                <p>{data.kFactor.limitation}</p>
                <p>
                  {data.kFactor.invitesInWindow} invites in the window,{' '}
                  {data.kFactor.matureInvites} of them at least{' '}
                  {data.kFactor.maturityDays} days old (younger invites are
                  censored rather than counted as failures).{' '}
                  {data.kFactor.retroAttributedInvites > 0 &&
                    `${data.kFactor.retroAttributedInvites} invite(s) marked converted describe a signup that pre-dates the invite — back-filled attribution, excluded from K.`}
                </p>
              </>
            }
          />

          {/* 9 — Referral rate among mature accounts. Deliberately separate
              from the K-factor card above: an account rate, not K. */}
          <MetricCard
            index={9}
            title={`Referral rate among accounts older than ${data.referralRate.maturityDays} days`}
            value={fmtPct(data.referralRate.ratePct)}
            sub={`${data.referralRate.accountsWithInvite} of ${data.referralRate.eligibleAccounts} mature accounts have sent at least one referral invite — not a K-factor`}
            details={
              <>
                <p>{data.referralRate.definition}</p>
                <p>
                  Eligible accounts signed up strictly more than{' '}
                  {data.referralRate.maturityDays * 24} hours ago (as-of, not a
                  trailing window); invites are counted whenever they were sent.{' '}
                  {data.referralRate.invitesFromEligibleAccounts} invites come
                  from those accounts, but an account that sent many counts
                  once.
                </p>
                <p>
                  Separately, and on weaker attribution:{' '}
                  {fmtPct(data.referralRate.convertedRatePct)} of mature
                  accounts (
                  {data.referralRate.accountsWithAttributableConversion} of{' '}
                  {data.referralRate.eligibleAccounts}) have an invitee who
                  completed onboarding after the invite was created. That share
                  does not feed the headline rate.
                </p>
                <p>{data.referralRate.limitation}</p>
              </>
            }
          />

          {/* 10 — Hold time accepted → closed: the tail that pickup wait
              can't see. */}
          <HumanTaskHoldCard index={10} data={data.humanTaskHold} />

          {/* 11 — Idle held tasks, with go-live markers for before/after. */}
          <IdleHeldTasksCard
            index={11}
            data={data.idleHeldTasks}
            releaseMarkers={data.releaseMarkers}
          />
        </div>
      )}

      {data && noActionOpen && (
        <NoActionModal
          days={data.days}
          onClose={() => setNoActionOpen(false)}
        />
      )}

      {data && humanCostOpen && (
        <HumanTaskCostModal
          data={data.humanTaskCost}
          days={data.days}
          onClose={() => setHumanCostOpen(false)}
        />
      )}
    </div>
  );
};

export default ScalingDashboard;
